import { print, DocumentNode } from "graphql";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { DocumentType } from "@gql/gql";
import axiosInstance from "./axiosInstance.ts";

/**
 * Execute a GraphQL query or mutation using axios (with auth configured)
 * @param document - The generated GraphQL document from @gql/gql
 * @param variables - Variables to pass to the GraphQL operation
 * @returns The data from the GraphQL response (unwrapped from the operation name)
 */
export async function executeGraphQL<TDocumentNode extends TypedDocumentNode<any, any>>(
  document: TDocumentNode,
  variables?: TDocumentNode extends TypedDocumentNode<any, infer TVariables> ? TVariables : never
): Promise<DocumentType<TDocumentNode>> {
  // Convert TypedDocumentNode to query string, TypedDocumentNode extends DocumentNode, so we can print it directly
  // Check if document is valid (codegen should populate this, but catch dev-time issues)
  if (!document || typeof document !== 'object' || !('kind' in document)) {
    throw new Error('Invalid GraphQL document. Make sure codegen has run and the document is properly generated.');
  }
  const query = print(document as DocumentNode);

  const response = await axiosInstance.post("/graphql", {
    query,
    variables,
  });

  // Check for GraphQL errors
  if (response.data.errors) {
    throw new Error(response.data.errors[0]?.message || "GraphQL error");
  }

  // GraphQL responses have structure: { data: { operationName: result } }
  // For single operations, there's exactly one key in the data object
  const data = response.data.data;
  if (!data) {
    throw new Error("No data returned from GraphQL query");
  }

  // Extract the operation result from the data object
  // GraphQL always returns exactly one top-level field per operation
  const keys = Object.keys(data);
  if (keys.length === 0) {
    throw new Error("Empty data object returned from GraphQL query");
  }
  if (keys.length > 1) {
    // This shouldn't happen for single operations, but handle it gracefully
    console.warn(`Multiple fields in GraphQL response: ${keys.join(", ")}. Using first field.`);
  }
  
  // Return the operation result (works for both single objects and arrays)
  // DocumentType extracts the actual result type from the TypedDocumentNode
  return data[keys[0]] as DocumentType<typeof document>;
}
